import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { isAdvancedOrderType, type AdvancedOrderType } from '@/constants/advancedOrder';

import { CheckoutModes, type CheckoutMode } from '@/hooks/useCheckoutMode';

const DEFAULT_ADVANCED_ORDER_TYPE: AdvancedOrderType = 'limit';

type UseAdvancedOrderTypeState = [AdvancedOrderType, (orderType: AdvancedOrderType) => void];

export function parseAdvancedOrderType(queryParam: unknown): AdvancedOrderType {
  return isAdvancedOrderType(queryParam) ? queryParam : DEFAULT_ADVANCED_ORDER_TYPE;
}

/**
 * Nested Advanced order type (`limit` | `stop-loss` | `take-profit`).
 * Selecting a type also enters Advanced. Omitted `orderType` still reads as Limit.
 */
export function useAdvancedOrderType(): UseAdvancedOrderTypeState {
  const router = useRouter();
  const orderType = parseAdvancedOrderType(router.query.orderType);

  const setAdvancedOrderType = useCallback(
    (nextOrderType: AdvancedOrderType) => {
      const query: {
        mode?: CheckoutMode;
        orderType?: AdvancedOrderType;
      } = {
        ...router.query,
        mode: CheckoutModes.advanced,
        orderType: nextOrderType,
      };

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  return [orderType, setAdvancedOrderType];
}
